import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('ar-DZ', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return d; }
};

const TransferAuditTrail = () => {
  const [data, setData] = useState({ count: 0, total_amount: 0, gaps: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ start_date: '', end_date: '' });
  const [uploadingId, setUploadingId] = useState(null);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.set('start_date', filters.start_date);
      if (filters.end_date) params.set('end_date', filters.end_date);
      const url = `${API}/audit/transfer-paper-gaps${params.toString() ? '?' + params.toString() : ''}`;
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error('فشل تحميل سجل التدقيق');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudit(); /* eslint-disable-next-line */ }, []);

  /** Allow the auditor to upload the missing signed receipt photo
   *  retroactively — closes the gap right from this screen. */
  const uploadMissingPhoto = async (gap, file) => {
    if (!file) return;
    setUploadingId(gap.id);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await axios.post(
        `${API}/cash-transfers/${gap.id}/upload-image`,
        fd,
        { headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        } },
      );
      toast.success(`✅ تم رفع الصورة لـ ${gap.reference_no || gap.id.slice(0, 8)}`);
      await fetchAudit();
    } catch (e) {
      toast.error('❌ فشل رفع الصورة: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploadingId(null);
    }
  };

  const reprintReceipt = (gap) => {
    const token = localStorage.getItem('token') || '';
    const url = `${API}/cash-transfers/${gap.id}/print?token=${encodeURIComponent(token)}&_=${Date.now()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="p-4 md:p-6 space-y-5" dir="rtl" data-testid="transfer-audit-page">
      {/* Banner */}
      <div className="bg-gradient-to-l from-red-900 via-red-800 to-amber-900 text-amber-200 p-6 rounded-2xl shadow-lg">
        <div className="flex flex-wrap justify-between items-end gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-wide">🔍 تدقيق الوصولات</h1>
            <p className="text-amber-100 mt-1 text-sm">
              التحويلات المؤكَّدة بدون صورة وصل موقَّع — ثغرات الأرشيف الورقي
            </p>
          </div>
          <div className="flex gap-3 text-center">
            <div className="bg-black/30 backdrop-blur rounded-xl px-4 py-2">
              <div className="text-3xl font-bold text-red-200" data-testid="audit-count">{data.count}</div>
              <div className="text-xs">ثغرة</div>
            </div>
            <div className="bg-black/30 backdrop-blur rounded-xl px-4 py-2">
              <div className="text-2xl font-bold text-amber-200" data-testid="audit-total">
                {(data.total_amount || 0).toLocaleString()} دج
              </div>
              <div className="text-xs">إجمالي المبلغ</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">🔎 تصفية حسب التاريخ</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-3 items-end">
          <div>
            <Label>من تاريخ</Label>
            <Input type="date" value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              data-testid="audit-start-date" />
          </div>
          <div>
            <Label>إلى تاريخ</Label>
            <Input type="date" value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              data-testid="audit-end-date" />
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchAudit} className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="audit-apply-filters">🔍 تطبيق</Button>
            <Button variant="outline"
              onClick={() => { setFilters({ start_date: '', end_date: '' }); setTimeout(fetchAudit, 50); }}>
              ↺ مسح
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>📋 قائمة الثغرات</span>
            {!loading && data.count === 0 && (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                ✅ لا توجد ثغرات — الأرشيف الورقي مكتمل
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-gray-500 py-8">جاري التحميل...</p>
          ) : data.gaps.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-3">🎉</div>
              <p className="text-lg font-bold text-green-700">رائع! كل تحويل مؤكَّد له وصل موقَّع.</p>
              <p className="text-sm text-gray-500 mt-1">لا حاجة لإجراء أي تدقيق ورقي.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-50 text-right">
                    <th className="p-2">🔖 المرجع</th>
                    <th className="p-2">💰 المبلغ</th>
                    <th className="p-2">🏢 الوكالة</th>
                    <th className="p-2">👨 المُحوّل</th>
                    <th className="p-2">👤 المُستَلِم</th>
                    <th className="p-2">📅 تاريخ التأكيد</th>
                    <th className="p-2">🛡️ المُؤكِّد</th>
                    <th className="p-2">⚙️ الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {data.gaps.map((gap) => (
                    <tr key={gap.id} className="border-t hover:bg-red-50"
                      data-testid={`audit-row-${gap.id}`}>
                      <td className="p-2 font-mono font-bold text-xs">
                        {gap.reference_no || (
                          <span className="text-gray-400">— قديم —</span>
                        )}
                      </td>
                      <td className="p-2 font-bold text-red-700">
                        {(gap.amount || 0).toLocaleString()} دج
                      </td>
                      <td className="p-2">{gap.agency_name || '—'}</td>
                      <td className="p-2">
                        <div className="text-xs">
                          <div className="font-medium">{gap.sender_name || '—'}</div>
                          {gap.sender_job && <div className="text-gray-500">{gap.sender_job}</div>}
                        </div>
                      </td>
                      <td className="p-2">
                        {gap.recipient_name ? (
                          <span className="text-xs">{gap.recipient_name}</span>
                        ) : (
                          <Badge variant="outline" className="text-orange-700 border-orange-300 text-[10px]">
                            غير مسجَّل
                          </Badge>
                        )}
                      </td>
                      <td className="p-2 text-xs whitespace-nowrap">{formatDate(gap.confirmation_date)}</td>
                      <td className="p-2 text-xs">
                        <div className="font-medium">{gap.confirmer_name || '—'}</div>
                        {gap.confirmer_job && <div className="text-gray-500">{gap.confirmer_job}</div>}
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline"
                            onClick={() => reprintReceipt(gap)}
                            className="text-amber-700 border-amber-400 hover:bg-amber-50 text-xs px-2 py-1"
                            data-testid={`audit-reprint-${gap.id}`}>
                            🖨️ وصل
                          </Button>
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" capture="environment"
                              className="hidden"
                              onChange={(e) => uploadMissingPhoto(gap, e.target.files?.[0])}
                              data-testid={`audit-upload-${gap.id}`} />
                            <span className={`inline-flex items-center text-xs px-2 py-1 rounded border ${
                              uploadingId === gap.id
                                ? 'bg-blue-100 text-blue-700 border-blue-300'
                                : 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                            }`}>
                              {uploadingId === gap.id ? '⏳ جارٍ الرفع...' : '📷 إغلاق الثغرة'}
                            </span>
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransferAuditTrail;
